// maintenanceText — メンテナンス再開予定時刻を JST 固定で人間可読文言にする
// 純関数モジュール (HomePage / ReJoinScreen で共有).
//
// `formatReopenText(waitUntilMs, nowMs)`:
//   - null                  → 「再開時刻は未定です」
//   - waitUntilMs <= nowMs   → 「まもなく再開予定です」
//   - それ以外              → 「<日時>に再開予定（<残り>）」
//
// 日時は **Asia/Tokyo 固定** で整形する (= 実行環境の TZ に依存しない).
// 「本日」判定は waitUntil と now を JST の年月日に落として比較する.
//   - 同日 → 「本日 HH:MM（JST）」
//   - 別日 → 「M月D日 HH:MM（JST）」
// 残りは分単位 (ceil):
//   - 60 分以上 → 「約N時間M分後」(M=0 のときは「約N時間後」)
//   - 60 分未満 → 「約N分後」
//
// 例:
//   formatReopenText(t, now) === '6月6日 20:00（JST）に再開予定（約45分後）'
//   formatReopenText(t, now) === '本日 20:00（JST）に再開予定（約2時間後）'

const JST = 'Asia/Tokyo';

/** waitUntil を JST の年月日 (`YYYY/MM/DD` 等の安定文字列) に落とす. */
const jstDateKey = (ms: number): string =>
  new Date(ms).toLocaleDateString('ja-JP', { timeZone: JST });

/** waitUntil を JST の `M月D日` に整形する. */
const jstMonthDay = (ms: number): string => {
  // ja-JP の numeric month/day は環境差で区切りが揺れる (`6/6` vs `6月6日`)
  // ことがあるため、Intl.DateTimeFormat の parts から月日を直接組み立てる.
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST,
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date(ms));
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${month}月${day}日`;
};

/** waitUntil を JST の `HH:MM` (24h・2桁) に整形する. */
const jstHourMinute = (ms: number): string =>
  new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(ms));

/** 残り時間 (分単位 ceil) を「約N時間M分後」/「約N分後」に整形する. */
const formatRemaining = (deltaMs: number): string => {
  const totalMin = Math.ceil(deltaMs / 60_000);
  if (totalMin < 60) {
    return `約${totalMin}分後`;
  }
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (mins === 0) {
    return `約${hours}時間後`;
  }
  return `約${hours}時間${mins}分後`;
};

/**
 * メンテ再開予定時刻を JST 固定の人間可読文言にする.
 *
 * @param waitUntilMs 再開予定 epoch ms (null = 不定)
 * @param nowMs       現在時刻 epoch ms (呼び出し側で `Date.now()` を渡す)
 */
export const formatReopenText = (waitUntilMs: number | null, nowMs: number): string => {
  if (waitUntilMs === null) {
    return '再開時刻は未定です';
  }
  const delta = waitUntilMs - nowMs;
  if (delta <= 0) {
    return 'まもなく再開予定です';
  }
  const sameDay = jstDateKey(waitUntilMs) === jstDateKey(nowMs);
  const dayPart = sameDay ? '本日' : jstMonthDay(waitUntilMs);
  const timePart = jstHourMinute(waitUntilMs);
  return `${dayPart} ${timePart}（JST）に再開予定（${formatRemaining(delta)}）`;
};
